/**
 * API client.
 *
 * Also the place clock skew is measured: every response carries a Date header, and
 * comparing it to the device clock tells us how much to trust a time captured offline.
 * A device whose clock is wrong by three hours would otherwise file doses in the wrong
 * place on the chart with nothing to indicate it.
 */
import type { AppConfig } from '../config.js';
import { currentAccessToken } from '../auth/session.js';
import { getMeta, setMeta } from '../offline/db.js';

const SKEW_KEY = 'clockSkewMs';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class OfflineError extends Error {
  constructor() {
    super('Offline');
    this.name = 'OfflineError';
  }
}

let cachedSkew: number | undefined;

export async function clockSkewMs(): Promise<number | null> {
  cachedSkew ??= await getMeta<number>(SKEW_KEY);
  return cachedSkew ?? null;
}

function recordSkew(dateHeader: string | null): void {
  if (!dateHeader) return;
  const serverMs = Date.parse(dateHeader);
  if (!Number.isFinite(serverMs)) return;
  // Positive means the device clock is ahead of the server.
  const skew = Date.now() - serverMs;
  cachedSkew = skew;
  void setMeta(SKEW_KEY, skew);
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Sent as Idempotency-Key so a retried write cannot duplicate. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function apiFetch<T>(
  config: AppConfig,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const token = await currentAccessToken(config);
  if (!token) throw new ApiError(401, 'Not signed in', 'unauthenticated');

  const headers: Record<string, string> = { authorization: `Bearer ${token}` };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  let response: Response;
  try {
    const init: RequestInit = { method: options.method ?? 'GET', headers };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;
    response = await fetch(`${config.apiUrl}${path}`, init);
  } catch {
    // A network failure is not an API failure. Callers treat it as "try again later"
    // rather than as a rejected write.
    throw new OfflineError();
  }

  recordSkew(response.headers.get('date'));

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const record = (payload ?? {}) as { error?: string; message?: string };
    throw new ApiError(response.status, record.message ?? response.statusText, record.error);
  }
  return payload as T;
}
